<script setup lang="ts">
import ThLarge from '@primeicons/vue/th-large';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Popover from 'primevue/popover';
import { ref } from 'vue';
import { useOperationalMapLayersStore } from '@/stores/useOperationalMapLayersStore';
import type { OperationalMapLayerToggleId } from '@/stores/useOperationalMapLayersStore';

defineProps<{
  options: ReadonlyArray<{ id: string; label: string }>;
}>();

const layersStore = useOperationalMapLayersStore();

const popoverRef = ref<{ toggle: (event: Event) => void } | null>(null);

function togglePopover(event: Event) {
  popoverRef.value?.toggle(event);
}

function setVisibility(id: string, value: boolean) {
  layersStore.setLayer(id as OperationalMapLayerToggleId, value);
}
</script>

<template>
  <div>
    <Button
      type="button"
      rounded
      severity="contrast"
      size="small"
      aria-haspopup="dialog"
      aria-label="Layer visibility"
      title="Layers"
      @click="togglePopover"
    >
      <template #icon>
        <ThLarge />
      </template>
    </Button>
    <Popover ref="popoverRef" class="w-[min(22rem,85vw)]">
      <div class="p-2">
        <p class="mb-2 text-xs font-medium text-surface-600">Layers</p>
        <div class="flex max-h-72 flex-col gap-1.5 overflow-y-auto pr-1">
          <div
            v-for="opt in options"
            :key="opt.id"
            class="flex items-start gap-2"
          >
            <Checkbox
              :input-id="`operational-map-layer-${opt.id}`"
              binary
              :model-value="
                layersStore.layerVisibility[
                  opt.id as OperationalMapLayerToggleId
                ] ?? false
              "
              @update:model-value="(v: boolean) => setVisibility(opt.id, v)"
            />
            <label
              :for="`operational-map-layer-${opt.id}`"
              class="cursor-pointer select-none text-xs leading-snug text-surface-700"
              >{{ opt.label }}</label
            >
          </div>
        </div>
      </div>
    </Popover>
  </div>
</template>
